import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { entities, media } from "../services/api";
import { formatRelativeTime, formatCurrency } from "../utils/dateUtils";
import {
  ProductIcon,
  ContainerIcon,
  LocationIcon,
  DollarIcon,
  ChevronRightIcon,
  RefreshIcon,
  ImageIcon,
} from "./Icons";

// Activity type icons and colors
const activityConfig = {
  product_added: {
    icon: ProductIcon,
    color: "var(--accent-primary)",
    bgColor: "rgba(59, 130, 246, 0.1)",
    label: "Added product",
  },
  product_sold: {
    icon: DollarIcon,
    color: "var(--success)",
    bgColor: "rgba(34, 197, 94, 0.1)",
    label: "Sold",
  },
  sale_undone: {
    icon: RefreshIcon,
    color: "var(--warning)",
    bgColor: "rgba(245, 158, 11, 0.1)",
    label: "Sale undone",
  },
  sale_updated: {
    icon: DollarIcon,
    color: "var(--accent-primary)",
    bgColor: "rgba(59, 130, 246, 0.1)",
    label: "Sale updated",
  },
  sold: {
    icon: DollarIcon,
    color: "var(--success)",
    bgColor: "rgba(34, 197, 94, 0.1)",
    label: "Sold",
  },
  container_added: {
    icon: ContainerIcon,
    color: "var(--purple)",
    bgColor: "rgba(168, 85, 247, 0.1)",
    label: "Added container",
  },
  location_added: {
    icon: LocationIcon,
    color: "var(--mint)",
    bgColor: "rgba(20, 184, 166, 0.1)",
    label: "Added location",
  },
  create: {
    icon: ProductIcon,
    color: "var(--accent-primary)",
    bgColor: "rgba(59, 130, 246, 0.1)",
    label: "Created",
  },
  trash: {
    icon: ProductIcon,
    color: "var(--danger)",
    bgColor: "rgba(239, 68, 68, 0.1)",
    label: "Trashed",
  },
  move: {
    icon: ChevronRightIcon,
    color: "var(--warning)",
    bgColor: "rgba(245, 158, 11, 0.1)",
    label: "Moved",
  },
  restore: {
    icon: RefreshIcon,
    color: "var(--info)",
    bgColor: "rgba(6, 182, 212, 0.1)",
    label: "Restored",
  },
  assign_qr: {
    icon: ProductIcon,
    color: "var(--purple)",
    bgColor: "rgba(168, 85, 247, 0.1)",
    label: "Linked QR code",
  },
  update: {
    icon: ProductIcon,
    color: "var(--accent-primary)",
    bgColor: "rgba(59, 130, 246, 0.1)",
    label: "Updated",
  },
  tags_changed: {
    icon: ProductIcon,
    color: "var(--warning)",
    bgColor: "rgba(245, 158, 11, 0.1)",
    label: "Tags updated",
  },
  listing_created: {
    icon: DollarIcon,
    color: "var(--purple)",
    bgColor: "rgba(168, 85, 247, 0.1)",
    label: "Listed for sale",
  },
  listing_updated: {
    icon: DollarIcon,
    color: "var(--accent-primary)",
    bgColor: "rgba(59, 130, 246, 0.1)",
    label: "Listing updated",
  },
  listing_deleted: {
    icon: DollarIcon,
    color: "var(--danger)",
    bgColor: "rgba(239, 68, 68, 0.1)",
    label: "Listing removed",
  },
  photo_added: {
    icon: ImageIcon,
    color: "var(--accent-primary)",
    bgColor: "rgba(59, 130, 246, 0.1)",
    label: "Photo added",
  },
  photo_deleted: {
    icon: ImageIcon,
    color: "var(--danger)",
    bgColor: "rgba(239, 68, 68, 0.1)",
    label: "Photo removed",
  },
  merge: {
    icon: ProductIcon,
    color: "var(--purple)",
    bgColor: "rgba(168, 85, 247, 0.1)",
    label: "Merged items",
  },
};

function ActivityFeed({ limit = 10 }) {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  const loadActivities = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await entities.activity(limit);
      setActivities(data || []);
    } catch (err) {
      console.error("Failed to load activity feed:", err);
      setError("Failed to load activity");
    } finally {
      setLoading(false);
    }
  };

  const handleActivityClick = (activity) => {
    if (activity.data?.entityId) {
      navigate(`/entity/${activity.data.entityId}`);
    }
  };

  if (loading) {
    return (
      <div className="activity-feed activity-feed-loading">
        <div className="spinner" style={{ width: 24, height: 24 }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activity-feed activity-feed-error">
        <p>{error}</p>
        <button onClick={loadActivities} className="btn btn-secondary btn-sm">
          <RefreshIcon size={14} /> Retry
        </button>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="activity-feed activity-feed-empty">
        <p>No recent activity</p>
        <span className="text-muted">
          Add products, make sales, or organize your inventory to see activity
          here.
        </span>
      </div>
    );
  }

  return (
    <div className="activity-feed">
      {activities.map((activity) => {
        const config =
          activityConfig[activity.type] || activityConfig.product_added;
        const IconComponent = config.icon;

        return (
          <div
            key={`${activity.type}-${activity.id}`}
            className="activity-item"
            onClick={() => handleActivityClick(activity)}
            style={{ cursor: activity.data?.entityId ? "pointer" : "default" }}
          >
            {/* Activity Icon */}
            <div
              className="activity-icon"
              style={{
                background: config.bgColor,
                color: config.color,
              }}
            >
              <IconComponent size={18} />
            </div>

            {/* Activity Content */}
            <div className="activity-content">
              <div className="activity-header">
                <span
                  className="activity-label"
                  style={{ color: config.color }}
                >
                  {config.label}
                </span>
                <span className="activity-time">
                  {formatRelativeTime(activity.timestamp)}
                </span>
              </div>

              <div className="activity-name">
                {activity.data?.name || "Unknown item"}
              </div>

              {/* Activity Details */}
              {activity.type === "product_sold" && activity.data?.salePrice && (
                <div className="activity-details">
                  <span className="activity-price activity-price-sale">
                    {formatCurrency(activity.data.salePrice)}
                  </span>
                  {activity.data.profit && (
                    <span
                      className="activity-profit"
                      style={{
                        color:
                          parseFloat(activity.data.profit) >= 0
                            ? "var(--success)"
                            : "var(--danger)",
                      }}
                    >
                      {parseFloat(activity.data.profit) >= 0 ? "+" : ""}
                      {formatCurrency(activity.data.profit)} profit
                    </span>
                  )}
                  {activity.data.channel && (
                    <span className="activity-channel">
                      {activity.data.channel}
                    </span>
                  )}
                </div>
              )}

              {activity.type === "product_added" && (
                <div className="activity-details">
                  {activity.data?.purchasePrice && (
                    <span className="activity-price">
                      Cost: {formatCurrency(activity.data.purchasePrice)}
                    </span>
                  )}
                  {activity.data?.retailPrice && (
                    <span className="activity-price">
                      Value: {formatCurrency(activity.data.retailPrice)}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Thumbnail for products with images */}
            {activity.data?.imagePath && (
              <div className="activity-thumbnail">
                <img
                  src={media.getImageUrl(activity.data.imagePath)}
                  alt=""
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ActivityFeed;
